import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./MixMatchBundle.module.css";
import BundlePreviewWrapper, {
    CustomSwatches,
    SubscriptionSelector,
    Swatches,
    VariantSelector,
    VariantSelectorV2,
} from "../../BundlePreviewWrapper/BundlePreviewWrapper";
import { ProductObj, MixMatchConfigInterface, VariantsObj } from "../../Types";
import {
    getPriceLabel,
    getFinalPrice,
    getDiscountValueWithSign,
    DiscountTypes,
    fetchCollectionProducts,
    buildMetafieldFromSwatchOptions,
    fetchStoreProducts,
    getDiscountValueWithSignString,
    generateRandomUniqueId,
} from "../../constants";
import { TStoreContext } from "../../../../context/types";
import ProductImageWrapper from "../../BundlePreviewWrapper/ProductImageWrapper";
import useIsMobile from "../../../../hooks/useIsMobile";
import ProductSubscriptionsPreview from "../../BundlePreviewWrapper/ProductSubscriptionsPreview";
import { FreeGiftComponent } from "../../BundlePreviewWrapper/BundlePreviewFooter";
import Spinner from "../../../../components/Icons/Spinner";
import { InspectorEditableBlock } from "../../CommonFields/InspectorEditableBlock";
import { getProductId, MAYUR_TEST_STORE } from "../../../constants";
import { RenderVariantInfo } from "../../BundleBuilder/BundleBuilderBundle/BBProductCard";
import BundleProgressBar from "../../BundleBuilder/BundleBuilderBundle/BundleProgressBar";
import { isDesktopUIForMixMatch } from "../../RenderOnStore/utilities";

const MixMatchBundle = (bundleConfig: MixMatchConfigInterface) => {
    return (
        !!bundleConfig && (
            <MixMatchPreview
                bundleConfig={{
                    ...bundleConfig,
                    ...(bundleConfig?.show_progress_bar && {
                        discount_options: bundleConfig.discount_options?.filter((d) => d.discountType !== "none"),
                    }),
                }}
                standAlone={true}
                appConfigContext={
                    {
                        currency: window.kefi_bundles.localization.currency,
                        money_format: window.kefi_bundles.money_format,
                        sft: window.kefi_bundles.sft,
                    } as TStoreContext
                }
            />
        )
    );
};

type Props_MixMatchPreview = {
    bundleConfig: MixMatchConfigInterface;
    standAlone?: boolean;
    appConfigContext: TStoreContext;
};

const ProductCardShimmer = ({ type }: { type: "collection" | "specific" }) => {
    return (
        <div
            style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "100px",
                fontSize: "20px",
                color: "#999999",
            }}
        >
            {type === "collection" ? "Select Collection" : "Select product(s)"}
        </div>
    );
};

// Helper function to flatten products as variants
const flatProductAsVariants = (products: ProductObj[], asVariantTrue: boolean): ProductObj[] => {
    if (!asVariantTrue) return products;

    return products.flatMap((product) =>
        product.variants.map((variant) => ({
            ...product,
            title: variant?.title || product?.title,
            parentName: product?.title,
            variants: [{ ...variant }],
            selectedVariants: [],
            images: variant?.image?.originalSrc
                ? [{ ...variant.image, altText: variant.title || product.title }]
                : product.images,
            originalOptions: product.options,
        })),
    );
};

const ProductCard = ({
    product,
    index,
    bundleConfig,
    appConfigContext,
    standAlone = false,
    setTotalQuantities,
    discountType,
    discountValue,
    onVariantChange,
    setUnavailableSelected,
    finalProductsList,
    setFinalProductsList,
    setTotalPrice,
    isMobile,
    type = "product",
    isRenderAsVariants = false,
    bundleWrapperWidth,
}: {
    product: ProductObj;
    index: number;
    bundleConfig: MixMatchConfigInterface;
    appConfigContext: TStoreContext;
    standAlone: boolean;
    setTotalQuantities: React.Dispatch<React.SetStateAction<number>>;
    discountType: DiscountTypes;
    discountValue: number;
    onVariantChange: (index: number, variant: VariantsObj) => void;
    unavailableSelected: number[];
    setUnavailableSelected: React.Dispatch<
        React.SetStateAction<{
            [key: number]: number[];
        }>
    >;
    finalProductsList: any;
    setFinalProductsList: any;
    setTotalPrice: any;
    isMobile: boolean;
    type?: string;
    isRenderAsVariants?: boolean;
    bundleWrapperWidth?: number;
}) => {
    const [, setDisPrice] = useState(0);
    const [origPrice, setOrigPrice] = useState(0);
    const [selectedVariants, setSelectedVariants] = useState<VariantsObj[]>([]);
    const [, setIsMultiVariant] = useState(false);
    // console._log(isMultiVariant);
    const { currency, money_format } = appConfigContext;
    const [showVariantName, setShowVariantName] = useState("");
    useEffect(() => {
        if (
            product?.variants?.length == 1 &&
            product.variants[0].title?.toLowerCase() != product?.title?.toLowerCase()
        ) {
            setShowVariantName(product.variants[0].title || "");
        } else {
            setShowVariantName("");
        }
    }, [product]);
    console._log("productInfo", product);

    const isDesktopView = isDesktopUIForMixMatch(bundleWrapperWidth ?? -1, bundleConfig?.bundle_type || "");

    const updatePrices = useCallback(() => {
        // Sum the prices of all selected variants
        const originalPrice = selectedVariants.reduce((total, variant) => {
            const price = bundleConfig?.apply_discount_on_compare_price ? +variant?.compareAtPrice : +variant.price;
            return total + price;
        }, 0);

        let discountPrice = originalPrice;
        const discount = discountValue || 0;

        switch (discountType) {
            case "percentage":
                // Discount is a percentage of the total price
                discountPrice -= discountPrice * (discount / 100);
                break;

            case "flat":
                // Flat discount applied directly to the total price
                discountPrice = discount < discountPrice ? discountPrice - discount : 0;
                break;

            case "fixed":
                // Fixed price after discount
                discountPrice = discount;
                break;

            case "none":
            default:
                // No discount
                discountPrice = originalPrice;
                break;
        }

        setDisPrice(Math.round(discountPrice * 100) / 100);
        setOrigPrice(Math.round(originalPrice * 100) / 100);
    }, [selectedVariants, discountType, discountValue, bundleConfig?.apply_discount_on_compare_price]);

    // Update selected variant and prices when variant changes
    useEffect(() => {
        product.selectedVariants = selectedVariants;
        onVariantChange(index, selectedVariants[selectedVariants.length - 1]);
        updatePrices();

        // if there is change in selected variant, update finalProductsList too
        setFinalProductsList((prev: any) => {
            const updatedList = prev
                .map((_product: any) => {
                    const { images, options, ...restt } = _product;
                    console._log(typeof images, typeof options);

                    const isMatch = isRenderAsVariants
                        ? _product.id == product.id && _product.variants?.[0]?.id === product.variants?.[0]?.id
                        : _product.id == product.id;

                    if (isMatch) {
                        return { ...restt, selectedVariants: selectedVariants };
                    }
                    return _product;
                })
                .filter((__product: any) => __product?.selectedVariants?.length !== 0); // Remove null products (those that have no variants left)
            return updatedList;
        });
    }, [selectedVariants, index, product, updatePrices]);

    useEffect(() => {
        if (type === "collection") {
            if (bundleConfig?.collections?.length == 0) {
                setSelectedVariants([]);
                setFinalProductsList([]);
            }
            return;
        }
        setIsMultiVariant(product.variants.length > 1);
        setSelectedVariants([]);
        setFinalProductsList([]);
    }, [product, bundleConfig?.collections]);

    // Handle product click for standalone or Shopify links
    const handleProductClick = (e: React.MouseEvent) => {
        e.preventDefault();
        if (standAlone && !window.location.pathname.includes(product.handle)) {
            const productURL = `https://${window.location.hostname}/products/${product.handle}`;
            window.open(productURL, "_blank");
        } else if (!window.location.pathname.includes(product.handle)) {
            const productURL = `https://${shopify.config.shop}/products/${product.handle}`;
            window.open(productURL, "_blank");
        }
    };

    const isMaxQuantityReached = (
        bundleConfig: MixMatchConfigInterface,
        productId: string,
        finalProductsList: any[],
        variantId?: string,
    ): boolean => {
        if (!bundleConfig?.limit_max_quantity_per_product) {
            return false;
        }

        const getCurrentProduct = () => {
            if (isRenderAsVariants && variantId) {
                return finalProductsList.find((p: any) => p.id === productId && p.variants?.[0]?.id === variantId);
            }
            return finalProductsList.find((p: any) => p.id === productId);
        };

        const currentProduct = getCurrentProduct();
        if (!currentProduct) {
            return false;
        }

        const count = currentProduct.selectedVariants?.length || 0;

        return count >= +(bundleConfig?.max_quantity_per_product || 0);
    };

    const maxQuantityReached = isMaxQuantityReached(
        bundleConfig,
        product.id,
        finalProductsList,
        isRenderAsVariants ? product.variants?.[0]?.id : undefined,
    );
    const updateQuantity = (change: number) => {
        if (change === +1 && maxQuantityReached) {
            return;
        }

        let updatedSv: VariantsObj[] = [];
        setSelectedVariants((prevSv) => {
            // selectedVariants updation
            updatedSv = [...prevSv];
            if (change == +1) {
                updatedSv.push(updatedSv[updatedSv?.length - 1]);
            } else {
                updatedSv.pop();
            }
            setFinalProductsList((prev: any) => {
                const updatedList = prev
                    .map((_product: any) => {
                        const { images, options, ...restt } = _product;
                        console._log(typeof images, typeof options);
                        const isMatch = isRenderAsVariants
                            ? _product.id == product.id && _product.variants?.[0]?.id === product.variants?.[0]?.id
                            : _product.id == product.id;

                        if (isMatch) {
                            return { ...restt, selectedVariants: updatedSv };
                        }
                        return _product;
                    })
                    .filter((__product: any) => __product?.selectedVariants?.length !== 0); // Remove null products (those that have no variants left)
                return updatedList;
            });
            return updatedSv;
        });
    };

    useEffect(() => {
        // Calculate total quantity
        if (finalProductsList.length === 0) {
            setTotalQuantities(0);
            setTotalPrice(0);
        } else {
            let tQty = 0;
            let tPrice = 0;
            finalProductsList.forEach((fpl: any) => {
                tQty += fpl?.selectedVariants?.length || 0;
                fpl.selectedVariants?.length &&
                    fpl.selectedVariants.forEach((sv: any) => {
                        tPrice = tPrice + (+sv?.price || 0);
                    });
            });
            setTotalPrice(+tPrice);
            setTotalQuantities(tQty);
        }
    }, [finalProductsList]);

    useEffect(() => {
        if (product.subscriptions && product.subscriptions.length > 0 && !product.oneTimePurchase) {
            product.isSubscribed = true;
        }
    }, [product, product.oneTimePurchase]);

    const renderPrice = () => {
        const availableVariant =
            product.variants?.find((variant) => variant?.availableForSale) || product.variants?.[0];

        const productPrice = +availableVariant?.price || 0;
        const compareAtPrice = +availableVariant?.compareAtPrice || 0;

        const shouldShowCompareAtPrice =
            bundleConfig?.price_display_type === "both_price_and_compare_at_price" && compareAtPrice > productPrice;

        return (
            <div className={styles.productMetaRight}>
                {bundleConfig?.show_prices ? (
                    <>
                        {!bundleConfig?.apply_discount_on_compare_price ? (
                            <div
                                style={{
                                    width: "max-content",
                                    display: "flex",
                                    flexDirection: isDesktopView ? "row" : "column",
                                    alignItems: !isDesktopView ? "flex-end" : "",
                                    gap: isDesktopView ? "5px" : 0,
                                }}
                            >
                                <InspectorEditableBlock
                                    bundleType={bundleConfig?.bundle_type}
                                    settingKey="productPrice"
                                >
                                    <div
                                        style={{
                                            color: bundleConfig?.bundles_design.highlighted_price_tag_color,
                                            fontSize:
                                                bundleConfig?.bundles_design.highlighted_price_tag_size < 50
                                                    ? bundleConfig?.bundles_design.highlighted_price_tag_size
                                                    : 50,
                                            fontWeight:
                                                bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[0],
                                            fontStyle:
                                                bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[1],
                                        }}
                                    >
                                        {getPriceLabel(getFinalPrice("none", 0, productPrice), money_format, currency)}
                                    </div>
                                </InspectorEditableBlock>
                                {shouldShowCompareAtPrice && (
                                    <InspectorEditableBlock
                                        bundleType={bundleConfig?.bundle_type}
                                        settingKey="productCompareAtPrice"
                                    >
                                        <div
                                            style={{
                                                display: isDesktopView ? "flex" : "",
                                                alignItems: isDesktopView ? "center" : "",
                                                textDecoration: "line-through",
                                                marginBottom: "2px",
                                                color: bundleConfig?.bundles_design.product_price_color,
                                                fontSize:
                                                    bundleConfig?.bundles_design.product_price_size < 50
                                                        ? bundleConfig?.bundles_design.product_price_size
                                                        : 50,
                                                fontWeight:
                                                    bundleConfig?.bundles_design.product_price_style?.split(" ")[0],
                                                fontStyle:
                                                    bundleConfig?.bundles_design.product_price_style?.split(" ")[1],
                                            }}
                                        >
                                            {getPriceLabel(
                                                getFinalPrice("none", 0, compareAtPrice),
                                                money_format,
                                                currency,
                                            )}
                                        </div>
                                    </InspectorEditableBlock>
                                )}
                            </div>
                        ) : (
                            <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productPrice">
                                <div
                                    style={{
                                        width: "max-content",
                                        color: bundleConfig?.bundles_design.highlighted_price_tag_color,
                                        fontSize:
                                            bundleConfig?.bundles_design.highlighted_price_tag_size < 50
                                                ? bundleConfig?.bundles_design.highlighted_price_tag_size
                                                : 50,
                                        fontWeight:
                                            bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[0],
                                        fontStyle:
                                            bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[1],
                                    }}
                                >
                                    {getPriceLabel(
                                        getFinalPrice("none", 0, compareAtPrice > 0 ? compareAtPrice : productPrice),
                                        money_format,
                                        currency,
                                    )}
                                </div>
                            </InspectorEditableBlock>
                        )}
                    </>
                ) : (
                    origPrice > 0 && (
                        <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productPrice">
                            <div
                                style={{
                                    width: "max-content",
                                    color: bundleConfig?.bundles_design.highlighted_price_tag_color,
                                    fontSize:
                                        bundleConfig?.bundles_design.highlighted_price_tag_size < 50
                                            ? bundleConfig?.bundles_design.highlighted_price_tag_size
                                            : 50,
                                    fontWeight: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[0],
                                    fontStyle: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[1],
                                }}
                            >
                                {getPriceLabel(getFinalPrice("none", 0, origPrice), money_format, currency)}
                            </div>
                        </InspectorEditableBlock>
                    )
                )}
            </div>
        );
    };

    const renderAddCta = (
        <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productAddButton">
            <button
                style={{
                    backgroundColor: bundleConfig?.bundles_design?.add_button_bg_color,
                    color: bundleConfig?.bundles_design?.add_button_text_color,
                    borderRadius: bundleConfig?.bundles_design?.add_button_border_radius,
                    border: `${bundleConfig?.bundles_design?.add_button_border_width}px solid ${bundleConfig?.bundles_design?.add_button_border_color}`,
                    fontSize: bundleConfig?.bundles_design?.add_button_text_size,
                    fontStyle: bundleConfig?.bundles_design?.add_button_text_style?.split(" ")[1],
                }}
                className={styles.addBtn}
                onClick={() => {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    let curSub: any = {};
                    if (product?.subscriptions?.length > 0 && product.subscriptions[0])
                        curSub = product?.subscriptions[0];
                    const targetVariant = isRenderAsVariants
                        ? product.variants[0]
                        : product.variants.find((vv) => vv.availableForSale == true) || product.variants[0];

                    const newSelectedVariant = {
                        ...targetVariant,
                        selling_plan:
                            product?.isSubscribed && curSub?.id
                                ? curSub?.id
                                : !product.oneTimePurchase
                                  ? product?.subscriptions &&
                                    product?.subscriptions.find((sub: any) => sub.checked === true)?.id
                                  : "",
                        ...(product?.isSubscribed && {
                            price:
                                getFinalPrice(curSub?.discountType, curSub?.discountValue, +targetVariant.price) + "",
                        }),
                        copyPrice: targetVariant.price,
                    };

                    setSelectedVariants([newSelectedVariant]);

                    setFinalProductsList((prev: any) => {
                        const { images, options, variants, ...restt } = product;
                        console._log(typeof images, typeof options, typeof variants);

                        const existingIndex = prev.findIndex((p: any) =>
                            isRenderAsVariants
                                ? p.id === product.id && p.variants?.[0]?.id === product.variants?.[0]?.id
                                : p.id === product.id,
                        );

                        if (existingIndex >= 0) {
                            // Update existing entry
                            const newArr = [...prev];
                            newArr[existingIndex] = {
                                ...newArr[existingIndex],
                                // variants: [targetVariant],
                                selectedVariants: [newSelectedVariant],
                            };
                            return newArr;
                        } else {
                            // Add new entry
                            return [
                                ...prev,
                                {
                                    ...restt,
                                    variants: [targetVariant],
                                    selectedVariants: [newSelectedVariant],
                                },
                            ];
                        }
                    });
                }}
            >
                {bundleConfig.add_button || "Add"}
            </button>
        </InspectorEditableBlock>
    );

    const renderQuantity = (
        <>
            <div className={styles.subInfo}>
                <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="quantityLabel">
                    <div
                        className={styles.qtyLabel}
                        style={{
                            color: bundleConfig?.bundles_design?.product_name_color,
                        }}
                    >
                        {bundleConfig.quantity_text || "Qty"}
                    </div>
                </InspectorEditableBlock>
                <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productAddButton">
                    <div className={styles.qtyValue}>
                        <button
                            className={styles.quantityButton}
                            onClick={() => updateQuantity(-1)}
                            style={{
                                backgroundColor: bundleConfig?.bundles_design?.add_button_bg_color,
                                color: bundleConfig?.bundles_design?.add_button_text_color,
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" style={{ height: 9, width: 9 }} viewBox="0 0 10 2">
                                <path
                                    fill="currentColor"
                                    fill-rule="evenodd"
                                    d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 1 1 0 1H1A.5.5 0 0 1 .5 1"
                                    clip-rule="evenodd"
                                ></path>
                            </svg>
                        </button>
                        <span
                            style={{
                                minWidth: 20,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                fontWeight: "600",
                                color: bundleConfig?.bundles_design?.product_name_color,
                            }}
                        >
                            {selectedVariants?.length}
                        </span>
                        <button
                            className={styles.quantityButton}
                            onClick={() => updateQuantity(+1)}
                            disabled={maxQuantityReached}
                            style={{
                                backgroundColor: bundleConfig?.bundles_design?.add_button_bg_color,
                                color: bundleConfig?.bundles_design?.add_button_text_color,
                                opacity: maxQuantityReached ? 0.5 : 1,
                                cursor: maxQuantityReached ? "default" : "pointer",
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" style={{ height: 9, width: 9 }} viewBox="0 0 10 10">
                                <path
                                    fill="currentColor"
                                    fill-rule="evenodd"
                                    d="M1 4.51a.5.5 0 0 0 0 1h3.5l.01 3.5a.5.5 0 0 0 1-.01V5.5l3.5-.01a.5.5 0 0 0-.01-1H5.5L5.49.99a.5.5 0 0 0-1 .01v3.5l-3.5.01z"
                                    clip-rule="evenodd"
                                ></path>
                            </svg>
                        </button>
                    </div>
                </InspectorEditableBlock>
            </div>
        </>
    );
    const userShopname = standAlone ? window.kefi_bundles.shop : shopify.config.shop;
    const effectiveMetafield = buildMetafieldFromSwatchOptions({
        product,
        bundleConfig: bundleConfig || {},
    });

    const isProductAvailable = useMemo(() => {
        return product.variants.some((vv) => (vv.quantityAvailable && vv.quantityAvailable > 0) || vv.availableForSale);
    }, [product]);

    const displayTitle = isRenderAsVariants ? product.parentName || product.title : product.title;

    return (
        <InspectorEditableBlock
            bundleType={bundleConfig.bundle_type}
            settingKey="productCard"
            tagsKey={product.tags?.toString()}
            productId={getProductId(product.id)}
        >
            {/* {index > 0 && <span className={styles.separator}></span>} */}

            <div
                style={{
                    padding: "14px 12px",
                    borderTopLeftRadius: index == 0 ? bundleConfig?.bundles_design?.border_radius : "0px",
                    borderTopRightRadius: index == 0 ? bundleConfig?.bundles_design?.border_radius : "0px",
                    borderBottomLeftRadius:
                        index == bundleConfig?.products.length - 1
                            ? bundleConfig?.bundles_design?.border_radius
                            : "0px",
                    borderBottomRightRadius:
                        index == bundleConfig?.products.length - 1
                            ? bundleConfig?.bundles_design?.border_radius
                            : "0px",
                    // borderRadius: bundleConfig?.bundles_design?.border_radius,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                }}
                className={styles.productCard}
            >
                <div className={styles.productInfoRight}>
                    <div className={styles.productMetaLeft}>
                        <ProductImageWrapper
                            product={{
                                ...product,
                                quantity: selectedVariants.length,
                            }}
                            handleProductClick={handleProductClick}
                            bundleConfig={bundleConfig}
                            isMobile={isMobile}
                            selectedVariants={selectedVariants}
                        />
                        <div
                            style={{
                                fontSize:
                                    bundleConfig?.bundles_design.product_name_size < 50
                                        ? bundleConfig?.bundles_design.product_name_size
                                        : 50,
                                color: bundleConfig?.bundles_design?.product_name_color,
                                fontWeight: bundleConfig?.bundles_design?.product_name_style?.split(" ")[0],
                                fontStyle: bundleConfig?.bundles_design?.product_name_style?.split(" ")[1],
                                cursor: "pointer",
                                width: "100%",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: 10,
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "1px",
                                    width: "100%",
                                }}
                            >
                                <div
                                    onClick={handleProductClick}
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        width: "100%",
                                        gap: "10px",
                                    }}
                                    className={styles.productTitle_Price}
                                >
                                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                        <InspectorEditableBlock
                                            bundleType={bundleConfig?.bundle_type}
                                            settingKey="productTitle"
                                            wrapWithDiv={true}
                                        >
                                            <span>{displayTitle}</span>
                                        </InspectorEditableBlock>
                                        {isDesktopView && renderPrice()}
                                        <div
                                            onClick={(e) => {
                                                e.stopPropagation();
                                            }}
                                            style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                width: "100%",
                                            }}
                                        >
                                            {selectedVariants?.length > 0 ? (
                                                renderQuantity
                                            ) : isProductAvailable ? (
                                                renderAddCta
                                            ) : (
                                                <span
                                                    style={{
                                                        fontSize: "12px",
                                                        color: "red",
                                                    }}
                                                >
                                                    {bundleConfig?.no_stock_text || "Out of Stock!"}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <span style={{ whiteSpace: "nowrap" }}>{!isDesktopView && renderPrice()}</span>
                                </div>

                                {(isRenderAsVariants || showVariantName) && (
                                    <RenderVariantInfo
                                        product={showVariantName ? { ...product, title: showVariantName } : product}
                                        style={{
                                            color: bundleConfig?.bundles_design?.variant_selector_text_color,
                                            fontSize: bundleConfig?.bundles_design?.variant_selector_text_size,
                                            fontWeight:
                                                bundleConfig?.bundles_design?.variant_selector_text_style?.split(
                                                    " ",
                                                )[0] ?? "450",
                                            fontStyle:
                                                bundleConfig?.bundles_design?.variant_selector_text_style?.split(
                                                    " ",
                                                )[1] ?? "normal",
                                        }}
                                        bundleType={bundleConfig?.bundle_type}
                                    />
                                )}
                            </div>

                            {selectedVariants.length > 0 && (
                                <ProductSubscriptionsPreview
                                    product={product}
                                    setSelectedVariants={setSelectedVariants}
                                    key={product.id}
                                    bundleConfig={bundleConfig}
                                />
                            )}
                        </div>
                    </div>
                </div>

                {!isRenderAsVariants && product?.variants.length > 1 && (
                    <div
                        style={{
                            marginTop: selectedVariants?.length > 0 ? "10px" : "0px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "10px",
                            width: "100%",
                        }}
                    >
                        {selectedVariants?.map((_: any, i: number) =>
                            userShopname === "typsy-beauty.myshopify.com" ? (
                                <Swatches
                                    bundlesDesign={bundleConfig?.bundles_design}
                                    key={i}
                                    variants={product.variants}
                                    handleChange={setSelectedVariants}
                                    selected={selectedVariants}
                                    options={product.options || []}
                                    productCardIndex={i}
                                    standAlone={standAlone}
                                />
                            ) : bundleConfig?.show_swatches && effectiveMetafield ? (
                                <CustomSwatches
                                    key={i}
                                    variants={product.variants}
                                    handleChange={setSelectedVariants}
                                    selected={selectedVariants}
                                    options={product.options || []}
                                    productCardIndex={i}
                                    standAlone={standAlone}
                                    metafield={effectiveMetafield}
                                    bundlesDesign={bundleConfig?.bundles_design}
                                    bundleConfig={bundleConfig}
                                />
                            ) : (
                                <VariantSelectorV2
                                    key={i}
                                    variants={product.variants}
                                    handleChange={setSelectedVariants}
                                    selected={selectedVariants}
                                    options={product.options || []}
                                    productCardIndex={i}
                                    standAlone={standAlone}
                                    metafield={effectiveMetafield}
                                    bundlesDesign={bundleConfig?.bundles_design}
                                    bundleConfig={bundleConfig}
                                />
                            ),
                        )}
                    </div>
                )}

                {product.isSubscribed && selectedVariants.length > 0 && (
                    <div style={{ width: "100%" }}>
                        <div style={{ margin: "5px 0" }}>
                            <span className={styles.separator}></span>
                        </div>

                        <SubscriptionSelector
                            product={product}
                            selectedVariants={selectedVariants}
                            setSelectedVariants={setSelectedVariants}
                        />
                    </div>
                )}
            </div>
        </InspectorEditableBlock>
    );
};

export const MixMatchPreview = ({ bundleConfig, standAlone, appConfigContext }: Props_MixMatchPreview) => {
    const [totalPrice, setTotalPrice] = useState(0);
    const { currency, money_format, sft } = appConfigContext;
    const [totalQuantities, setTotalQuantities] = useState<number>(0);
    const [discountType, setDiscountType] = useState<DiscountTypes>("none");
    const [discountValue, setDiscountValue] = useState<number>(0);
    const [maxDiscountAllowed, setMaxDiscountAllowed] = useState<number | null>(null);
    const [compareAtPrice, setCompareAtPrice] = useState(0);
    const [tierSuccessMessage, setTierSuccessMessage] = useState("");
    const [finalSuccessMessage, setFinalSuccessMessage] = useState("");

    // updated state to handle both products and page info
    const [collectionBasedProducts, setCollectionBasedProducts] = useState<ProductObj[]>([]);
    const [loadingMore, setLoadingMore] = useState(false);
    const [, setPage] = useState(1);
    const [pageInfo, setPageInfo] = useState<any>(null); // 🆕 state to store page info
    const fetchCount = 5;

    const bundleRef = useRef<HTMLDivElement>(null);
    const { isMobile } = useIsMobile();
    const loadMoreTriggerRef = useRef<HTMLDivElement | null>(null);
    const [unavailableSelected, setUnavailableSelected] = useState<{
        [key: number]: number[];
    }>({});

    const userShopname = standAlone ? window.kefi_bundles.shop : shopify.config.shop || "";
    const bundleWrapperRef = useRef<HTMLDivElement>(null);
    const bundleWrapperWidth = bundleWrapperRef.current?.offsetWidth ?? -1;

    const displayProducts = useMemo(() => {
        if (bundleConfig?.mixmatch_product_selection === "specific" && bundleConfig?.render_as_variants) {
            const flattened = flatProductAsVariants(bundleConfig.products || [], true);
            return flattened;
        }
        return bundleConfig.products || [];
    }, [bundleConfig?.products, bundleConfig?.mixmatch_product_selection, bundleConfig?.render_as_variants]);

    const [collectionIndex, setCollectionIndex] = useState(0);

    const loadMoreCollectionProducts = async () => {
        if (loadingMore || !bundleConfig.collections || !bundleConfig.collections.length) return; // Prevent multiple fetches

        const currentCollection = bundleConfig.collections[collectionIndex];
        if (!currentCollection) {
            setPageInfo((prev: any) => ({ ...prev, hasNextPage: false }));
            return;
        }

        const gid = currentCollection.id;
        const id = gid?.split("/").pop() || "";

        const { products, pageInfo: newPageInfo } = await fetchCollectionProducts(
            id,
            sft,
            setLoadingMore,
            standAlone,
            fetchCount,
            pageInfo?.endCursor || null,
        );

        setCollectionBasedProducts((prevProducts) => {
            const uniqueNew = products.filter((p: any) => !prevProducts.some((ep: any) => ep.id === p.id));
            return [...prevProducts, ...uniqueNew];
        });

        if (newPageInfo?.hasNextPage) {
            setPageInfo(newPageInfo);
        } else if (collectionIndex + 1 < bundleConfig.collections.length) {
            setCollectionIndex(collectionIndex + 1);
            setPageInfo({ hasNextPage: true, endCursor: null });
        } else {
            setPageInfo(newPageInfo);
        }
        setLoadingMore(false);
    };

    useEffect(() => {
        if (bundleConfig?.mixmatch_product_selection !== "collection" || bundleConfig?.collections?.length == 0) {
            setCollectionBasedProducts([]);
            setPageInfo(null);
            setLoadingMore(false);
            setPage(1);
            setCollectionIndex(0);
            return;
        }
    }, [bundleConfig?.collections, bundleConfig?.mixmatch_product_selection, bundleConfig?.render_product_pages_type]);

    useEffect(() => {
        if (bundleConfig?.mixmatch_product_selection === "collection" && bundleConfig?.collections?.length > 0) {
            setCollectionBasedProducts([]);
            setPageInfo({ hasNextPage: true, endCursor: null });
            setCollectionIndex(0);
            setLoadingMore(false);
        }
    }, [bundleConfig?.collections]);

    useEffect(() => {
        if (bundleConfig?.mixmatch_product_selection === "collection" && bundleConfig?.collections?.length > 0) {
            if (
                collectionBasedProducts.length === 0 &&
                !loadingMore &&
                pageInfo?.endCursor === null &&
                collectionIndex === 0
            ) {
                loadMoreCollectionProducts();
            }
        }
    }, [
        collectionBasedProducts,
        collectionIndex,
        loadingMore,
        pageInfo,
        bundleConfig?.collections,
        bundleConfig?.mixmatch_product_selection,
    ]);

    useEffect(() => {
        const trigger = loadMoreTriggerRef.current;
        const scrollContainer = bundleRef.current;

        if (!trigger || !scrollContainer || !pageInfo?.hasNextPage) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !loadingMore) {
                    observer.disconnect();
                    loadMoreCollectionProducts();
                }
            },
            {
                root: scrollContainer,
                threshold: 0.2,
            },
        );

        observer.observe(trigger); // observe latest trigger

        return () => {
            observer.unobserve(trigger); // cleanup previous trigger
            observer.disconnect();
        };
    }, [pageInfo?.hasNextPage, loadMoreTriggerRef, collectionBasedProducts.length]); // 🆕 observe new trigger on update

    useEffect(() => {
        if (collectionBasedProducts?.length) {
            window?.localStorage.setItem("mm_handle", collectionBasedProducts[0]?.handle);
        }
    }, [collectionBasedProducts]);

    const getDiscountOption = () => {
        return bundleConfig?.discount_options.find((option) => {
            const isWithinQuantityRange =
                totalQuantities >= option.quantity &&
                (option.allowExtraItems || totalQuantities <= (option.maxQuantity || option.quantity));
            return isWithinQuantityRange;
        });
    };

    useEffect(() => {
        const discountOption = getDiscountOption();

        if (discountOption) {
            setDiscountType(discountOption.discountType || "none");
            setDiscountValue(discountOption.discountValue || 0);
            setMaxDiscountAllowed(discountOption.max_discount_allowed ?? null);
        } else {
            setDiscountType("none");
            setDiscountValue(0);
            setMaxDiscountAllowed(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalQuantities, bundleConfig?.discount_options, bundleConfig?.products]);

    const getNextTierOption = () => {
        return bundleConfig?.discount_options.find((option) => totalQuantities < option.quantity);
    };

    const getExcessQuantityMessage = (
        bundleConfig: MixMatchConfigInterface,
        totalQuantities: number,
        currency: string,
        money_format: string,
    ): { hasExcess: boolean; message: string; shouldResetProgress: boolean } => {
        const currentOption = bundleConfig?.discount_options.find((option) => {
            const isWithinQuantityRange =
                totalQuantities >= option.quantity &&
                (option.allowExtraItems || totalQuantities <= (option.maxQuantity || option.quantity));
            return isWithinQuantityRange;
        });

        // If there's a valid option, no excess
        if (currentOption) {
            return { hasExcess: false, message: "", shouldResetProgress: false };
        }

        // Find the tier that was exceeded
        const exceededOption = bundleConfig?.discount_options
            .slice()
            .reverse()
            .find((option) => {
                return totalQuantities > (option.maxQuantity || option.quantity) && !option.allowExtraItems;
            });

        if (exceededOption) {
            const maxAllowed = exceededOption.maxQuantity || exceededOption.quantity;
            const excessQuantity = totalQuantities - maxAllowed;

            const discountString = getDiscountValueWithSignString(
                exceededOption.discountType,
                exceededOption.discountValue || 0,
                currency,
                money_format,
            );

            const message =
                excessQuantity === 1
                    ? `Remove ${excessQuantity} product to get ${discountString} discount!`
                    : `Remove ${excessQuantity} product(s) to get ${discountString} discount!`;
            return {
                hasExcess: true,
                message: message,
                shouldResetProgress: true,
            };
        }

        return { hasExcess: false, message: "", shouldResetProgress: false };
    };

    const excessInfo = useMemo(
        () => getExcessQuantityMessage(bundleConfig, totalQuantities, currency, money_format),
        [bundleConfig, totalQuantities, currency, money_format],
    );

    // Effect to handle tier and final success messages
    useEffect(() => {
        const allOptions = bundleConfig?.discount_options || [];

        if (allOptions.length > 0) {
            const lastTierQty = allOptions[allOptions.length - 1]?.quantity || 0;

            // Check if final tier reached and showFinalSuccessMessage is enabled
            if (totalQuantities >= lastTierQty && allOptions[0]?.showFinalSuccessMessage) {
                setFinalSuccessMessage(allOptions[0]?.finalSuccessMessage || "");
            } else {
                setFinalSuccessMessage("");
            }
        }

        const discountOption = getDiscountOption();
        const nextTierOption = getNextTierOption();

        if (discountOption) {
            setDiscountType(discountOption.discountType || "none");
            setDiscountValue(discountOption.discountValue || 0);
            setMaxDiscountAllowed(discountOption.max_discount_allowed ?? null);
        } else {
            setDiscountType("none");
            setDiscountValue(0);
            setMaxDiscountAllowed(null);
        }

        // Handle tier success message for next tier
        if (nextTierOption?.ShowTierSuccessMessage && nextTierOption?.TierSuccessMessage) {
            const remainingProducts = nextTierOption.quantity - totalQuantities;
            const discountString = getDiscountValueWithSignString(
                nextTierOption.discountType,
                nextTierOption.discountValue || 0,
                currency,
                money_format,
            );

            const msg = nextTierOption.TierSuccessMessage.replace(
                "{noOfRemainingProducts}",
                remainingProducts.toString(),
            ).replace("{discount}", discountString as string);

            setTierSuccessMessage(msg);
        } else {
            setTierSuccessMessage("");
        }
    }, [totalQuantities, bundleConfig?.discount_options, currency, money_format]);

    const [freeGiftSelectedVariants, setFreeGiftSelectedVariants] = useState<VariantsObj[]>([]);
    const [finalProductsList, setFinalProductsList] = useState<ProductObj[]>([]);

    useEffect(() => {
        setFinalProductsList([]);
    }, [bundleConfig?.mixmatch_product_selection]);

    const updatedBundleConfig = {
        ...bundleConfig,
        products: [
            ...finalProductsList,
            ...freeGiftSelectedVariants.map((variant) => ({
                selectedVariants: [{ ...variant, free: true }], // Wrapping each variant in selectedVariants array
            })),
        ],
        discount_type: discountType,
        discount_value: discountValue,
        max_allowed_discount: maxDiscountAllowed,
        ...(bundleConfig?.disable_atc_until_threshold && {
            disableAddToCartButton: totalQuantities < (bundleConfig?.atc_threshold || 1),
            tooltipMessage: (bundleConfig.atc_tooltip_text || "").replace(
                "{itemCount}",
                String(bundleConfig.atc_threshold || 1),
            ),
        }),
        ...([
            "quickstart-f02489ba.myshopify.com",
            // "msg-september2025-entwicklungsumgebung.myshopify.com",
            // "mein-schoener-garten-shop.myshopify.com",
            MAYUR_TEST_STORE,
        ].includes(userShopname) && {
            disableAddToCartButton: !getDiscountOption(),
        }),
    };

    useEffect(() => {
        const hasActiveFreeGiftOption = bundleConfig?.discount_options?.some(
            (option) =>
                totalQuantities >= option.quantity &&
                (option.allowExtraItems || totalQuantities <= option.maxQuantity) &&
                option.allow_free_gifts &&
                option.free_gifts &&
                option.free_gift_text,
        );

        if (!hasActiveFreeGiftOption) {
            setFreeGiftSelectedVariants([]);
        }
    }, [bundleConfig?.discount_options, totalQuantities]);
    const [allStoreProducts, setAllStoreProducts] = useState<any[]>([]);
    const [loadingAllProducts, setLoadingAllProducts] = useState<boolean>(false);

    const isProductInStock = useCallback((product: ProductObj) => {
        return product.variants.some((v) => v.availableForSale);
    }, []);

    const displayProductsFiltered = useMemo(() => {
        if (bundleConfig.hide_out_of_stock_products) {
            return displayProducts.filter(isProductInStock);
        }
        return displayProducts;
    }, [displayProducts, bundleConfig.hide_out_of_stock_products, isProductInStock]);

    const collectionBasedProductsFiltered = useMemo(() => {
        if (bundleConfig.hide_out_of_stock_products) {
            return collectionBasedProducts.filter(isProductInStock);
        }
        return collectionBasedProducts;
    }, [collectionBasedProducts, bundleConfig.hide_out_of_stock_products, isProductInStock]);

    const allStoreProductsFiltered = useMemo(() => {
        if (bundleConfig.hide_out_of_stock_products) {
            return allStoreProducts.filter(isProductInStock);
        }
        return allStoreProducts;
    }, [allStoreProducts, bundleConfig.hide_out_of_stock_products, isProductInStock]);

    const allProductsOutOfStock = useMemo(() => {
        // If hide_out_of_stock_products is true, we should check if the filtered list is empty but the original wasn't or if all in original are oos
        // The user want: "if all the products are out of stock then [show text]"
        // This implies checking the ORIGINAL lists.
        let originalEmpty = true;
        let allOOS = true;

        if (bundleConfig?.mixmatch_product_selection === "all") {
            if (allStoreProducts.length > 0) {
                originalEmpty = false;
                allOOS = allStoreProducts.every((p) => !isProductInStock(p));
            }
        } else if (bundleConfig?.mixmatch_product_selection === "collection") {
            if (collectionBasedProducts.length > 0) {
                originalEmpty = false;
                allOOS = collectionBasedProducts.every((p) => !isProductInStock(p));
            }
        } else {
            if (displayProducts.length > 0) {
                originalEmpty = false;
                allOOS = displayProducts.every((p) => !isProductInStock(p));
            }
        }

        return !originalEmpty && allOOS;
    }, [
        bundleConfig?.mixmatch_product_selection,
        allStoreProducts,
        collectionBasedProducts,
        displayProducts,
        isProductInStock,
    ]);

    useEffect(() => {
        const getAllProducts = async () => {
            if (bundleConfig?.mixmatch_product_selection === "all") {
                setLoadingAllProducts(true);
                try {
                    const products = await fetchStoreProducts(sft, standAlone, 100);
                    setAllStoreProducts(products || []);
                } catch (error) {
                    console.error("Error fetching all products:", error);
                } finally {
                    setLoadingAllProducts(false);
                }
            }
        };

        getAllProducts();
    }, [bundleConfig?.mixmatch_product_selection]);

    useEffect(() => {
        const newCp = finalProductsList?.reduce((fp, curr) => {
            const sV = curr.selectedVariants.reduce((acc, cur) => {
                const price = bundleConfig?.apply_discount_on_compare_price ? +cur?.compareAtPrice : +cur?.price;
                return acc + price;
            }, 0);
            return fp + sV;
        }, 0);

        setCompareAtPrice(newCp);
    }, [finalProductsList, bundleConfig?.apply_discount_on_compare_price]);

    const mnm_part_of = bundleConfig?.bundle_as_product?.variant_id;

    if (!bundleConfig) return null;

    if (allProductsOutOfStock && standAlone && bundleConfig.hide_out_of_stock_products) {
        return null;
    }

    console._log("finalProductsList", finalProductsList);

    // Check if render_as_variants is enabled for specific products
    const isRenderAsVariants =
        bundleConfig?.mixmatch_product_selection === "specific" && bundleConfig?.render_as_variants;

    return (
        <div ref={bundleWrapperRef}>
            <BundlePreviewWrapper
                // @ts-expect-error required
                bundleConfig={updatedBundleConfig}
                standAlone={standAlone}
                mnm_part_of={mnm_part_of}
                totalPrice={bundleConfig?.apply_discount_on_compare_price ? compareAtPrice : totalPrice}
                currency={currency}
                money_format={money_format}
                compareAtPriceTotal={compareAtPrice}
            >
                <>
                    {/* Top tabs for quantity ranges */}
                    {(bundleConfig?.products?.length > 0 ||
                        (bundleConfig?.mixmatch_product_selection == "collection" &&
                            collectionBasedProducts.length > 0) ||
                        (bundleConfig?.mixmatch_product_selection == "all" && allStoreProducts?.length > 0)) &&
                        (bundleConfig?.show_progress_bar ? (
                            bundleConfig?.discount_options?.length > 0 && (
                                <>
                                    {excessInfo.hasExcess && (
                                        <div
                                            className={styles.excessMessageContainer}
                                            style={{
                                                padding: "10px 15px",
                                                color:
                                                    bundleConfig?.bundles_design?.discount_tabs_discount_label_color ||
                                                    "#2e7d32",
                                                fontSize:
                                                    bundleConfig?.bundles_design?.discount_tabs_quantity_label_size ||
                                                    14,
                                                fontWeight:
                                                    bundleConfig?.bundles_design?.discount_tabs_quantity_label_style?.split(
                                                        " ",
                                                    )[0] || "500",
                                                fontStyle:
                                                    bundleConfig?.bundles_design?.discount_tabs_quantity_label_style?.split(
                                                        " ",
                                                    )[1] || "normal",
                                                textAlign: "center",
                                                lineHeight: "1.4",
                                            }}
                                        >
                                            {excessInfo.message}
                                        </div>
                                    )}

                                    {!excessInfo.hasExcess && tierSuccessMessage && (
                                        <div
                                            className={styles.tierSuccessMessageContainer}
                                            style={{
                                                padding: "10px 15px",
                                                color:
                                                    bundleConfig?.bundles_design?.discount_tabs_discount_label_color ||
                                                    "#2e7d32",
                                                fontSize:
                                                    bundleConfig?.bundles_design?.discount_tabs_quantity_label_size ||
                                                    14,
                                                fontWeight:
                                                    bundleConfig?.bundles_design?.discount_tabs_quantity_label_style?.split(
                                                        " ",
                                                    )[0] || "500",
                                                fontStyle:
                                                    bundleConfig?.bundles_design?.discount_tabs_quantity_label_style?.split(
                                                        " ",
                                                    )[1] || "normal",
                                                textAlign: "center",
                                                lineHeight: "1.4",
                                            }}
                                        >
                                            {tierSuccessMessage}
                                        </div>
                                    )}

                                    {!excessInfo.hasExcess && finalSuccessMessage && (
                                        <div
                                            className={styles.finalSuccessMessageContainer}
                                            style={{
                                                padding: "10px 15px",
                                                color:
                                                    bundleConfig?.bundles_design?.discount_tabs_discount_label_color ||
                                                    "#2e7d32",
                                                fontSize:
                                                    bundleConfig?.bundles_design?.discount_tabs_quantity_label_size ||
                                                    14,
                                                fontWeight:
                                                    bundleConfig?.bundles_design?.discount_tabs_quantity_label_style?.split(
                                                        " ",
                                                    )[0] || "500",
                                                fontStyle:
                                                    bundleConfig?.bundles_design?.discount_tabs_quantity_label_style?.split(
                                                        " ",
                                                    )[1] || "normal",
                                                textAlign: "center",
                                                lineHeight: "1.4",
                                            }}
                                        >
                                            {finalSuccessMessage}
                                        </div>
                                    )}

                                    <div
                                        className={styles.progressBarContainer}
                                        style={{
                                            width: "95%",
                                            minHeight: "32px",
                                            padding: "8px 0",
                                        }}
                                    >
                                        <BundleProgressBar
                                            bundleConfig={bundleConfig}
                                            totalPrice={totalPrice}
                                            totalQuantity={excessInfo.shouldResetProgress ? 0 : totalQuantities}
                                            currency={currency}
                                            money_format={money_format}
                                        />
                                    </div>
                                </>
                            )
                        ) : (
                            <div className={styles.quantityTabs}>
                                {bundleConfig?.discount_options?.map((option, index) => {
                                    const isActive =
                                        totalQuantities >= option.quantity &&
                                        (option.allowExtraItems || totalQuantities <= option.maxQuantity);
                                    let label = "";

                                    if (option.addRange) {
                                        if (option.allowExtraItems && option.quantity === option.maxQuantity) {
                                            label = `${option.quantity}+ ${bundleConfig?.option_items_text || "items"}`;
                                        } else if (option.quantity === option.maxQuantity) {
                                            label = `${option.quantity} ${bundleConfig?.option_items_text || "items"}`;
                                        } else {
                                            label = `${option.quantity}-${option.maxQuantity} ${
                                                bundleConfig?.option_items_text || "items"
                                            }`;
                                        }
                                    } else {
                                        if (option.allowExtraItems && option.quantity === option.maxQuantity) {
                                            label = `${option.quantity}+ ${bundleConfig?.option_items_text || "items"}`;
                                        } else {
                                            label = `${option.quantity} ${bundleConfig?.option_items_text || "items"}`;
                                        }
                                    }

                                    const getDiscountLabel = () => {
                                        if (option.discountValue < 0) {
                                            return "";
                                        }

                                        switch (option.discountType) {
                                            case "percentage":
                                                if (option.discountValue == 0) {
                                                    return "";
                                                }
                                                if (option.max_discount_allowed) {
                                                    return (
                                                        <>
                                                            <span>
                                                                {option.discountValue}%{" "}
                                                                {bundleConfig?.option_off_text || "Off"}
                                                            </span>
                                                            ({bundleConfig.option_upto_text || "upto"}&nbsp;
                                                            {getPriceLabel(
                                                                getFinalPrice("none", 0, option.max_discount_allowed),
                                                                money_format,
                                                                currency,
                                                            )}
                                                            )
                                                        </>
                                                    );
                                                }
                                                if (option.discountValue == 100) {
                                                    return bundleConfig?.free_text || "FREE";
                                                }
                                                return `${option.discountValue}% ${
                                                    bundleConfig?.option_off_text || "Off"
                                                }`;

                                            case "fixed":
                                                if (option.discountValue == 0) {
                                                    return bundleConfig?.free_text || "FREE";
                                                }
                                                return getDiscountValueWithSign(
                                                    option.discountType,
                                                    option.discountValue,
                                                    currency,
                                                    money_format,
                                                    bundleConfig?.footer_text,
                                                );
                                            case "flat":
                                                if (option.discountValue == 0) {
                                                    return "";
                                                }
                                                return getDiscountValueWithSign(
                                                    option.discountType,
                                                    option.discountValue,
                                                    currency,
                                                    money_format,
                                                    bundleConfig?.footer_text,
                                                );

                                            default:
                                                return "";
                                        }
                                    };

                                    return (
                                        <InspectorEditableBlock
                                            bundleType={bundleConfig?.bundle_type}
                                            settingKey="quantityTab"
                                        >
                                            <div
                                                key={option.id + index}
                                                className={`${styles.tab} ${isActive ? styles.active : ""}`}
                                                style={{
                                                    borderWidth:
                                                        bundleConfig?.bundles_design?.discount_tabs_border_width ?? 2,
                                                    borderRadius:
                                                        bundleConfig?.bundles_design?.discount_tabs_border_radius,
                                                    backgroundColor: isActive
                                                        ? bundleConfig?.bundles_design?.discount_tabs_selected_bg_color
                                                        : bundleConfig?.bundles_design?.discount_tabs_bg_color,
                                                    borderColor: isActive
                                                        ? bundleConfig?.bundles_design
                                                              ?.discount_tabs_selected_border_color
                                                        : bundleConfig?.bundles_design?.discount_tabs_border_color,
                                                }}
                                            >
                                                <InspectorEditableBlock
                                                    bundleType={bundleConfig?.bundle_type}
                                                    settingKey="quantityLabel"
                                                >
                                                    <span
                                                        className={styles.quantityLabel}
                                                        style={{
                                                            fontSize: `${
                                                                bundleConfig?.bundles_design
                                                                    ?.discount_tabs_quantity_label_size < 50
                                                                    ? bundleConfig?.bundles_design
                                                                          ?.discount_tabs_quantity_label_size
                                                                    : 50
                                                            }px`,
                                                            color: isActive
                                                                ? bundleConfig?.bundles_design
                                                                      ?.discount_tabs_selected_text_color
                                                                : bundleConfig?.bundles_design
                                                                      ?.discount_tabs_discount_label_color,
                                                            fontWeight:
                                                                bundleConfig?.bundles_design?.discount_tabs_quantity_label_style?.split(
                                                                    " ",
                                                                )[0],
                                                            fontStyle:
                                                                bundleConfig?.bundles_design?.discount_tabs_quantity_label_style?.split(
                                                                    " ",
                                                                )[1],
                                                        }}
                                                    >
                                                        {label}
                                                    </span>
                                                </InspectorEditableBlock>
                                                <InspectorEditableBlock
                                                    bundleType={bundleConfig?.bundle_type}
                                                    settingKey="discountValue"
                                                >
                                                    <span
                                                        className={styles.discountLabel}
                                                        style={{
                                                            fontSize: `${
                                                                bundleConfig?.bundles_design
                                                                    ?.discount_tabs_discount_label_size < 50
                                                                    ? bundleConfig?.bundles_design
                                                                          ?.discount_tabs_discount_label_size
                                                                    : 50
                                                            }px`,
                                                            color: isActive
                                                                ? bundleConfig?.bundles_design
                                                                      ?.discount_tabs_selected_text_color
                                                                : bundleConfig?.bundles_design
                                                                      ?.discount_tabs_discount_label_color,
                                                            fontWeight:
                                                                bundleConfig?.bundles_design?.discount_tabs_quantity_label_style?.split(
                                                                    " ",
                                                                )[0],
                                                            fontStyle:
                                                                bundleConfig?.bundles_design?.discount_tabs_quantity_label_style?.split(
                                                                    " ",
                                                                )[1],
                                                        }}
                                                    >
                                                        {getDiscountLabel()}

                                                        {option?.allow_free_gifts &&
                                                            option?.free_gifts &&
                                                            option?.free_gifts?.length > 0 && (
                                                                <>
                                                                    {" "}
                                                                    {bundleConfig?.offer_free_gift_text ||
                                                                        "+ Free Gift"}
                                                                </>
                                                            )}
                                                    </span>
                                                </InspectorEditableBlock>
                                            </div>
                                        </InspectorEditableBlock>
                                    );
                                })}
                            </div>
                        ))}
                    <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productCard">
                        <div
                            className={styles.products}
                            ref={bundleRef}
                            style={{
                                borderRadius: bundleConfig?.bundles_design?.border_radius,
                                border: `${bundleConfig?.bundles_design?.border_width}px solid ${bundleConfig?.bundles_design?.product_border_color}`,
                                backgroundColor: bundleConfig?.bundles_design.product_background_color,
                                maxHeight: "450px",
                                overflow: "auto",
                                WebkitOverflowScrolling: "auto",
                                overscrollBehavior: "auto",
                            }}
                        >
                            {allProductsOutOfStock && bundleConfig?.hide_out_of_stock_products && !standAlone && (
                                <div
                                    style={{
                                        padding: "20px",
                                        textAlign: "center",
                                        color: "#d32f2f",
                                        fontWeight: "500",
                                    }}
                                >
                                    All products in bundle are currently out of stock. The bundle will be hidden on the
                                    storefront if "Hide Out of Stock Products" is enabled.
                                </div>
                            )}

                            {bundleConfig?.mixmatch_product_selection === "all" ? (
                                <>
                                    {loadingAllProducts ? (
                                        <ProductCardShimmer type="specific" />
                                    ) : allStoreProductsFiltered.length > 0 ? (
                                        allStoreProductsFiltered.map((product, index) => (
                                            <ProductCard
                                                key={product?.id + product?.variants[0]?.id}
                                                index={index}
                                                product={product}
                                                bundleConfig={bundleConfig}
                                                appConfigContext={appConfigContext}
                                                standAlone={standAlone || false}
                                                setTotalQuantities={setTotalQuantities}
                                                discountType={discountType}
                                                discountValue={discountValue}
                                                onVariantChange={() => {}}
                                                unavailableSelected={unavailableSelected[index] || []}
                                                setUnavailableSelected={setUnavailableSelected}
                                                finalProductsList={finalProductsList}
                                                setFinalProductsList={setFinalProductsList}
                                                setTotalPrice={setTotalPrice}
                                                isMobile={isMobile}
                                                bundleWrapperWidth={bundleWrapperWidth}
                                            />
                                        ))
                                    ) : (
                                        <div style={{ padding: "10px", color: "#696969" }}>No products available.</div>
                                    )}
                                </>
                            ) : null}

                            {bundleConfig?.mixmatch_product_selection ===
                            "all" ? null : bundleConfig?.mixmatch_product_selection === "collection" &&
                              collectionBasedProductsFiltered.length > 0 ? (
                                <>
                                    {collectionBasedProductsFiltered.map((product, index) => (
                                        <ProductCard
                                            key={product?.id + product?.variants[0]?.id}
                                            index={index}
                                            product={product}
                                            bundleConfig={bundleConfig}
                                            appConfigContext={appConfigContext}
                                            standAlone={standAlone || false}
                                            setTotalQuantities={setTotalQuantities}
                                            discountType={discountType}
                                            discountValue={discountValue}
                                            onVariantChange={() => {}}
                                            unavailableSelected={unavailableSelected[index] || []}
                                            setUnavailableSelected={setUnavailableSelected}
                                            finalProductsList={finalProductsList}
                                            setFinalProductsList={setFinalProductsList}
                                            setTotalPrice={setTotalPrice}
                                            isMobile={isMobile}
                                            type="collection"
                                        />
                                    ))}
                                </>
                            ) : displayProductsFiltered?.length ? (
                                <>
                                    {displayProductsFiltered.map((product, index) => (
                                        <ProductCard
                                            key={product?.id + product?.variants[0]?.id}
                                            index={index}
                                            product={product}
                                            bundleConfig={bundleConfig}
                                            appConfigContext={appConfigContext}
                                            standAlone={standAlone || false}
                                            setTotalQuantities={setTotalQuantities}
                                            discountType={discountType}
                                            discountValue={discountValue}
                                            onVariantChange={() => {}}
                                            unavailableSelected={unavailableSelected[index] || []}
                                            setUnavailableSelected={setUnavailableSelected}
                                            finalProductsList={finalProductsList}
                                            setFinalProductsList={setFinalProductsList}
                                            setTotalPrice={setTotalPrice}
                                            isMobile={isMobile}
                                            isRenderAsVariants={isRenderAsVariants}
                                        />
                                    ))}
                                </>
                            ) : (
                                <ProductCardShimmer
                                    type={
                                        bundleConfig?.mixmatch_product_selection === "collection"
                                            ? "collection"
                                            : "specific"
                                    }
                                />
                            )}
                            <div className={styles.loadMoreWrapper}>
                                {loadingMore ? (
                                    <div
                                        style={{
                                            width: "100%",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignContent: "center",
                                            padding: 10,
                                        }}
                                    >
                                        <Spinner color={"#939393"} size={50} />
                                        <span
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                fontSize: 12,
                                                color: "#939393",
                                            }}
                                        >
                                            {bundleConfig?.load_more_products_text
                                                ? bundleConfig.load_more_products_text
                                                : "Loading more products..."}
                                        </span>
                                    </div>
                                ) : (
                                    collectionBasedProducts.length > 0 &&
                                    pageInfo?.hasNextPage && (
                                        <span
                                            ref={loadMoreTriggerRef}
                                            style={{
                                                width: "100%",
                                                display: "flex",
                                                height: "10px",
                                                visibility: "hidden",
                                            }}
                                        />
                                    )
                                )}
                            </div>
                        </div>
                    </InspectorEditableBlock>

                    {bundleConfig?.discount_options?.map((option) => {
                        const isActive =
                            totalQuantities >= option.quantity &&
                            (option.allowExtraItems || totalQuantities <= option.maxQuantity);

                        return (
                            isActive &&
                            option.allow_free_gifts &&
                            option.free_gifts &&
                            option.free_gift_text && (
                                <div style={{ marginTop: 15 }}>
                                    <FreeGiftComponent
                                        products={option?.free_gifts || []}
                                        bundleConfig={
                                            {
                                                ...bundleConfig,
                                                free_gift_text: option?.free_gift_text || "",
                                                bundles_design: {
                                                    ...bundleConfig.bundles_design,
                                                    add_to_cart_border_radius:
                                                        bundleConfig?.bundles_design.border_radius,
                                                },
                                            } as any
                                        }
                                        standAlone={standAlone}
                                        targetForm={""}
                                        setFreeGiftSelectedVariants={setFreeGiftSelectedVariants}
                                        money_format={money_format}
                                        currency={currency}
                                    />
                                </div>
                            )
                        );
                    })}
                </>
            </BundlePreviewWrapper>
        </div>
    );
};

export default MixMatchBundle;
